import { type PersistOptions, persist, createJSONStorage } from 'zustand/middleware'
import { create } from 'zustand'

import { createContactsStore } from './contacts'
import { createCurrentWalletStore } from './currentWallet'
import { createCustomTokensStore } from './customTokens'
import { createTokenStore } from './tokens'
import { createDevModeStore } from './devMode'
import { createNftBalancesStore } from './nftBalances'
import { createNotificationWindowStore } from './notificationWindow'
import { createPendingRequestsStore } from './pendingRequests'
import { createPermissionsStore } from './permissions'
import { createProtectedCanistersStore } from './protectedCanisters'
import { createSelectedTabStore } from './selectedTab'
import { createSnsStore } from './sns'
import { createSwapSlippageStore } from './swapSlippage'
import { createTokenBalancesStore } from './tokenBalances'
import { createUserTokensStore } from './userTokens'
import { createWalletBTCAddressesStore } from './walletBTCAddresses'
import { createWalletPropertiesStore } from './walletProperties'
import { createBalanceChangesStore } from './balanceChange'
import { createLocalesStore } from './locales'
import { createActivitySettingsStore } from './activitySettings'
import { createMigrateToV2Store } from './migrateToV2'
import { createUserConvertStore } from './userConverts'
import { createUserActivityStore } from './userActivity'
import { LocalStorage } from '../storage'

import type { Mutate, StoreApi } from 'zustand/vanilla'
import type { StateStorage } from 'zustand/middleware'
import { createSelectedChainStore } from './selectedChain'
import { createBackupSeedStore } from './backupSeed'
import { createPromoStore } from './promo'

type Initializer<TState> = Parameters<typeof persist<TState>>[0]

export type StoreWithPersist<TState> = Mutate<StoreApi<TState>, [['zustand/persist', unknown]]> & {
  initializer: Initializer<TState>
}

export function createStore<TState>(
  storage: StateStorage,
  initializer: Initializer<TState>,
  { persist: persistOptions }: { persist?: PersistOptions<TState> } = {}
) {
  const name = `plug.store.${persistOptions?.name}`
  return Object.assign(
    create(
      persist(initializer, {
        ...persistOptions,
        name,
        storage: persistOptions ? createJSONStorage(() => storage) : undefined,
      })
    ),
    { initializer }
  )
}

const stateStorage: StateStorage = {
  getItem: async (key: string): Promise<string | null> => {
    return (await LocalStorage.get(key)) || null
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await LocalStorage.set(key, value)
  },
  removeItem: async (key: string): Promise<void> => {
    await LocalStorage.remove(key)
  },
}

export const {
  useContacts,
  useCurrentWallet,
  useCustomTokens,
  useTokens,
  useDevMode,
  useNftBalances,
  useNotificationWindows,
  usePendingRequests,
  usePermissions,
  useProtectedCanisters,
  useSelectedTab,
  useSns,
  useSwapSlippage,
  useTokenBalances,
  useUserTokens,
  useWalletBTCAddresses,
  useWalletProperties,
  useBalanceChanges,
  useActivitySettings,
  useLocales,
  useMigrateToV2,
  useUserConverts,
  useSelectedChain,
  useUserActivity,
  useBackupSeed,
  usePromo,
} = {
  useContacts: createContactsStore(stateStorage),
  useCurrentWallet: createCurrentWalletStore(stateStorage),
  useCustomTokens: createCustomTokensStore(stateStorage),
  useTokens: createTokenStore(stateStorage),
  useDevMode: createDevModeStore(stateStorage),
  useNftBalances: createNftBalancesStore(stateStorage),
  useNotificationWindows: createNotificationWindowStore(stateStorage),
  usePendingRequests: createPendingRequestsStore(stateStorage),
  usePermissions: createPermissionsStore(stateStorage),
  useProtectedCanisters: createProtectedCanistersStore(stateStorage),
  useSelectedTab: createSelectedTabStore(stateStorage),
  useSns: createSnsStore(stateStorage),
  useSwapSlippage: createSwapSlippageStore(stateStorage),
  useTokenBalances: createTokenBalancesStore(stateStorage),
  useUserTokens: createUserTokensStore(stateStorage),
  useWalletBTCAddresses: createWalletBTCAddressesStore(stateStorage),
  useWalletProperties: createWalletPropertiesStore(stateStorage),
  useBalanceChanges: createBalanceChangesStore(stateStorage),
  useActivitySettings: createActivitySettingsStore(stateStorage),
  useLocales: createLocalesStore(stateStorage),
  useMigrateToV2: createMigrateToV2Store(stateStorage),
  useUserConverts: createUserConvertStore(stateStorage),
  useSelectedChain: createSelectedChainStore(stateStorage),
  useUserActivity: createUserActivityStore(stateStorage),
  useBackupSeed: createBackupSeedStore(stateStorage),
  usePromo: createPromoStore(stateStorage),
}
